import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { AuthService } from "@/services";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/core/logo";
import { showToast } from "@/hooks/useToast";
import { useAuth } from "@/contexts";

export const Route = createFileRoute("/auth/wait-verify-email")({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      email: (search.email as string) || "",
      telefone: (search.telefone as string) || undefined,
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const { email, telefone } = Route.useSearch();
  const [isChecking, setIsChecking] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const { user, saveAuth } = useAuth();

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const handleCheck = async () => {
    // Prefer telefone from context user, otherwise from query
    const phoneToUse = user?.telefone || telefone;

    setIsChecking(true);
    try {
      // Try to finalize login (this will issue token if both verified)
      const result = await AuthService.finalizeLogin({ telefone: phoneToUse, email });

      if (result?.access) {
        // Login finalized
        saveAuth(result as any);
        navigate({ to: "/app" });
        showToast.success("Login realizado com sucesso!");
        return;
      }

      // If still not verified, inform user
      if (result?.requiresEmailVerification) {
        showToast.error("E-mail ainda não verificado. Verifique sua caixa de entrada.");
        return;
      }

      if (result?.requiresPhoneVerification) {
        // Redirect to phone verification if required
        const phone = result.user?.telefone || phoneToUse;
        navigate({ to: "/auth/verify-phone", search: { telefone: phone! } });
        return;
      }

      // Fallback: check status
      const resp = await AuthService.checkEmailStatus(email);
      if (resp.verified) {
        showToast.success("E-mail verificado! Volte ao login para prosseguir.");
        navigate({ to: "/auth/identify" });
      } else {
        showToast.error("E-mail ainda não verificado. Verifique sua caixa de entrada.");
      }
    } catch (err) {
      showToast.error(err instanceof Error ? err.message : "Erro ao verificar e-mail");
    } finally {
      setIsChecking(false);
    }
  };

  const handleResendEmail = async () => {
    setIsResending(true);
    try {
      await AuthService.resendVerificationEmail(email);
      setCooldown(60);
      showToast.success("E-mail reenviado com sucesso!");
    } catch (err) {
      showToast.error(err instanceof Error ? err.message : "Erro ao reenviar e-mail");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative" style={{
      backgroundImage: `url("/authentication-bg.svg")`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
    }}>
      <div className="w-full max-w-md relative z-10">
        <div className="bg-white rounded-4xl shadow-xl p-8 text-center">
          <div className="flex items-center justify-center mb-6">
            <Logo className="h-12" isTraditionalLogo />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">Verifique seu e-mail</h1>
          <p className="text-sm text-gray-600 mb-4">Enviamos um link de ativação para {email}. Abra seu e-mail e clique no link para ativar sua conta.</p>

          <p className="text-xs text-gray-500 mb-6">Não encontrou o e-mail? Confira sua pasta de <strong>spam</strong> ou <strong>lixeira</strong>.</p>

          <div className="space-y-3">
            <Button className="w-full" disabled={isChecking} onClick={handleCheck}>
              {isChecking ? "Verificando..." : "Já verifiquei"}
            </Button>

            <Button
              variant="outline"
              className="w-full"
              disabled={isResending || cooldown > 0}
              onClick={handleResendEmail}
            >
              {cooldown > 0 ? `Reenviar em ${cooldown}s` : "Reenviar e-mail"}
            </Button>

            <Button variant="outline" className="w-full" onClick={() => navigate({ to: '/auth/identify' })}>
              Voltar ao login
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
